import { Component, OnInit, Input, inject } from '@angular/core';
import { Validators,FormControl,FormGroup,FormBuilder,ValidatorFn } from '@angular/forms';
import { ConfirmationService } from "primeng/api";
import { UserContextService } from "../../../services/safeguard/user-context.service";
import { SharedService } from "../../../shared/services/shared.service";
import { BrandService } from '../../../services/config/brand.service';
import { CsrService } from '../../services/csr/csr-service';
import { CustomLoggerService } from '../../../services/monitor/custom-logger.service';
import { IBrand } from '../../../shared/interfaces/brand';
import { IEnrollmentInvitationRequest } from '../../interfaces/stats/enrollment-invitation-request';
import { AccountField } from '../../../classes/account-field';
import { ICsrInputField } from '../../interfaces/csr/csr-input-field';

@Component({
  selector: 'app-csr-account',
  standalone: false,
  templateUrl: './csr-account.component.html',
  styleUrls: ['./csr-account.component.css']
})
export class CsrAccountComponent implements OnInit {

  private readonly customLoggerService = inject(CustomLoggerService);

  accountForm!: FormGroup;
  emailForm!: FormGroup;
  smsForm!: FormGroup;

  openBrandSelectionDialog: boolean = false;
  isBrandSelectionButtonShown: boolean = false;
  isSpinnerHidden: boolean = true;
  isSecondaryAccountIdShown: boolean = false;
  isTretiaryAccountIdShown: boolean = false;
  isImagePreviewShown: boolean = false;
  isGuestImagePreviewShown: boolean = false;
  isAccountDetailsShown: boolean = false;
  isCsrInstructShown: boolean = false;
  isSendEnrollmentLinksShown: boolean = false;
  isCoachingShown: boolean = false;
  isDeregisterAccountShown: boolean = false;
  isSmsInvitationEnabled: boolean = false;

  primaryTooltip: string = "Please enter a valid account number";
  primaryErrorText: string = "Account Number is required";

  useSecondaryAccountId: boolean = false;
  secondaryAccountIdName: string = '';
  secondaryAccountIdLabel: string = 'Secondary Account Id:';
  secondaryTooltip: string = "";
  secondaryErrorText: string = "";

  useTertiaryAccountId: boolean = false;
  tertiaryAccountIdName: string = '';
  tertiaryAccountIdLabel: string = 'Tertiary Account Id:';
  tertiaryTooltip: string = "";
  tertiaryErrorText: string = "";

  coachingBodyEmail: string = '';
  coachingBodySMS: string = '';
  coachingHeaderEmail: string = '';
  coachingHeaderSMS: string = '';
  coachingBody: string = '';
  coachingHeader: string = '';
  deregCmdSupported: boolean = false;

  currentBrand: IBrand;
  clientBrandCode!: string;
  brandName!: string;
  baseUrl!: string;

  brandImage: any;
  guestBrandImage: any;

  resultMsg!: string;
  titleMessage: string = "CSR";

  csrInstructHeader: string = 'Walletron Background';
  csrInstructBody: string = '';

  accountFound: boolean = false;
  accountRegistered: boolean = false;
  message: string = '';

  @Input() accountFields: AccountField<string>[] = [];
  @Input() csrInvitationParams: AccountField<string>[] = [];

  // Customer identity verification fields
  evFields: AccountField<string>[] = [];

  constructor(
    private fb: FormBuilder,
    private userContextService: UserContextService,
    private confirmationService: ConfirmationService,
    private sharedService: SharedService,
    private brandService: BrandService,
    private csrService: CsrService
    ) {
      this.currentBrand = this.sharedService.getDummyBrand();
    }

  ngOnInit(): void {

    this.accountForm = this.fb.group({
      'accountNumber': new FormControl('', [Validators.required, Validators.maxLength(45)]),
      'secondaryAccountId': new FormControl(''),
      'tertiaryAccountId': new FormControl('')
    });

    this.emailForm = this.fb.group({
      'customerEmailAddress': new FormControl('', [Validators.required])
    });

    this.smsForm = this.fb.group({
      'customerPhoneNo': new FormControl('', [Validators.required, Validators.pattern("^(\\([0-9]{3}\\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$")])
    });

    this.newBrandCleanup();
    this.newAccountCleanup();

    if (this.sharedService.isCurrentBrandKnown()) {
      this.currentBrand = this.sharedService.getCurrentBrand();
      this.titleMessage = this.makeTitleMessage(this.currentBrand);
      this.getBrandProperties(this.currentBrand);
      this.openBrandSelectionDialog = false;
      this.getCsrConfiguration();
    } else {
      let grantedBrands = this.userContextService.getUserBrands();
      if (grantedBrands) {
        if (grantedBrands.length == 1) {
          const brand0 = grantedBrands[0];
          if (brand0) {
            this.setSingleBrand(brand0);
          }
        } else {
          this.isBrandSelectionButtonShown = true;
          this.openBrandSelectionDialog = true;
        }
      } else {
        this.isBrandSelectionButtonShown = true;
        this.openBrandSelectionDialog = true;
      }
    }
  }

  onChooseBrandClick(event: any) {
    this.openBrandSelectionDialog = true;
  }

  chooseBrand(item: IBrand) {
    this.openBrandSelectionDialog = false;
    if (item != null) {
      this.getBrandProperties(item);
      this.currentBrand = item;
      this.titleMessage = this.makeTitleMessage(this.currentBrand);
      this.sharedService.setCurrentBrand(item);
      this.getCsrConfiguration();
    }
  }

  getBrandProperties(brand: IBrand) {
    this.brandName = brand.brandName || '';
    this.clientBrandCode = brand.brandCode || '';
    this.baseUrl = brand.baseUrl || '';
  }

  setSingleBrand(brandCode: string) {
    this.isSpinnerHidden = false;
    this.brandService.getBrandWithRegion(brandCode).subscribe(brand => {
      this.currentBrand = brand;
      this.titleMessage = this.makeTitleMessage(this.currentBrand);
      this.getBrandProperties(brand);
      this.isSpinnerHidden = true;
      this.getCsrConfiguration();
    });
  }

  getCsrConfiguration() {
    this.isSpinnerHidden = false;
    this.newBrandCleanup();
    this.newAccountCleanup();
    this.evFields = [];
    Object.keys(this.accountForm.controls).forEach(key => {
      if (key != 'accountNumber' && key != 'secondaryAccountId' && key != 'tertiaryAccountId') {
        this.accountForm.removeControl(key);
      }
    });
    this.csrService.getCsrConfiguration(this.currentBrand.brandCode, this.currentBrand.baseUrl)
    .subscribe({
      next: (result) => {
        if (result) {
          if (result.showCsrInstruct) {
            this.csrInstructHeader = result.csrInstructHeader || '';
            this.csrInstructBody = result.csrInstructBody || '';
            this.isCsrInstructShown = true;
          }
          if (result.csrInputFields) {
            for (let csrInputField of result.csrInputFields) {
              let validators: ValidatorFn[] = [];
              validators.push(Validators.required);
              if (csrInputField.maxLength) { validators.push(Validators.maxLength(csrInputField.maxLength)); }
              if (csrInputField.minLength) { validators.push(Validators.minLength(csrInputField.minLength)); }
              if (csrInputField.fieldPattern) { validators.push(Validators.pattern(csrInputField.fieldPattern)); }
              if (csrInputField.fieldRank == "1") {
                const ctrl = this.accountForm.get('accountNumber');
                if (ctrl) { ctrl.clearValidators(); ctrl.setValidators(validators); }
                this.primaryTooltip = csrInputField.toolTip || '';
                this.primaryErrorText = csrInputField.validationMessage || '';
              }
              if (csrInputField.fieldRank == "2") {
                const ctrl = this.accountForm.get('secondaryAccountId');
                if (ctrl) { ctrl.clearValidators(); ctrl.setValidators(validators); }
                this.secondaryAccountIdName = csrInputField.fieldName;
                this.secondaryAccountIdLabel = csrInputField.fieldLabel;
                this.secondaryTooltip = csrInputField.toolTip || '';
                this.secondaryErrorText = csrInputField.validationMessage || '';
                this.isSecondaryAccountIdShown = true;
                this.useSecondaryAccountId = result.useSecondaryAccountId;
              }
              if (csrInputField.fieldRank == "3") {
                const ctrl = this.accountForm.get('tertiaryAccountId');
                if (ctrl) { ctrl.clearValidators(); ctrl.setValidators(validators); }
                this.tertiaryAccountIdName = csrInputField.fieldName;
                this.tertiaryAccountIdLabel = csrInputField.fieldLabel;
                this.tertiaryTooltip = csrInputField.toolTip || '';
                this.tertiaryErrorText = csrInputField.validationMessage || '';
                this.isTretiaryAccountIdShown = true;
                this.useTertiaryAccountId = result.useTertiaryAccountId;
              }
            }
          } else {
            const ctrl1 = this.accountForm.get('accountNumber');
            if (ctrl1) { ctrl1.clearValidators(); ctrl1.setValidators([Validators.required, Validators.maxLength(45), Validators.minLength(2)]); }
            this.primaryTooltip = "Please enter a valid account number";
            this.primaryErrorText = "Account Number is required";
            this.useSecondaryAccountId = result.useSecondaryAccountId;
            if (result.useSecondaryAccountId) {
              this.isSecondaryAccountIdShown = true;
              this.secondaryAccountIdName = result.secondaryAccountIdName || '';
              this.secondaryAccountIdLabel = result.secondaryAccountIdLabel || '';
            }
            const ctrl2 = this.accountForm.get('secondaryAccountId');
            if (ctrl2) { ctrl2.clearValidators(); }
            this.secondaryTooltip = '';
            this.useTertiaryAccountId = result.useTertiaryAccountId;
            if (result.useTertiaryAccountId) {
              this.isTretiaryAccountIdShown = true;
              this.tertiaryAccountIdName = result.tertiaryAccountIdName || '';
              this.tertiaryAccountIdLabel = result.tertiaryAccountIdLabel || '';
            }
            const ctrl3 = this.accountForm.get('tertiaryAccountId');
            if (ctrl3) { ctrl3.clearValidators(); }
            this.tertiaryTooltip = '';
          }
          this.coachingBodyEmail = result.coachingBodyEmail || '';
          this.coachingBodySMS = result.coachingBodySMS || '';
          this.coachingHeaderEmail = result.coachingHeaderEmail || '';
          this.coachingHeaderSMS = result.coachingHeaderSMS || '';
          this.isCoachingShown = false;
          this.deregCmdSupported = result.deregCmdSupported || false;
          if (result.evFields) {
            result.evFields.forEach(item => {
              if (item.variableName && !this.accountForm.contains(item.variableName)) {
                this.evFields.push({ label: item.placeholder.literal || '', value: '', key: item.variableName || '', options: [] });
                this.accountForm.addControl(item.variableName, new FormControl('', [Validators.required]));
              }
            });
          }
          if (result.smsInvitationEnabled) { this.isSmsInvitationEnabled = true; }
          this.isSpinnerHidden = true;
        }
        this.isSpinnerHidden = true;
      },
      error: (error) => {
        this.customLoggerService.logException('csr.getCsrConfiguration()', 'CSR-1', error.message, 'CSR');
      },
      complete: () => {}
    });
  }

  onSubmit(value: string) {
    this.isSpinnerHidden = false;
    this.newAccountCleanup();
    let accountNumber = this.accountForm.value.accountNumber;
    let secondaryAccountId = this.accountForm.value.secondaryAccountId;
    let tertiaryAccountId = this.accountForm.value.tertiaryAccountId;
    if (this.evFields) {
      this.evFields.forEach(item => {
        if (!secondaryAccountId) { secondaryAccountId = this.accountForm.get(item.key)?.value; }
        else if (!tertiaryAccountId) { tertiaryAccountId = this.accountForm.get(item.key)?.value; }
      });
    }
    this.csrService.getCsrAccountLookup(this.currentBrand.brandCode, this.currentBrand.baseUrl,
      accountNumber, secondaryAccountId, tertiaryAccountId)
      .subscribe({
        next: (result) => {
          if (result) {
            this.accountFound = result.accountFound;
            this.accountRegistered = result.accountRegistered;
            this.message = result.message || '';
            if (result.accountFields) {
              result.accountFields.forEach(item => {
                this.accountFields.push({ label: item.key, value: item.value, key: item.key, options: [] });
              });
            }
            this.csrInvitationParams = [];
            if (result.csrInvitationParams && result.csrInvitationParams.length > 0) {
              result.csrInvitationParams.forEach(item => {
                this.csrInvitationParams.push({ label: item.key, value: item.value, key: item.key, options: [] });
              });
            }
            if (this.accountFound) {
              this.isSendEnrollmentLinksShown = true;
              if (result.emailAddress) { this.emailForm.setValue({customerEmailAddress: result.emailAddress}); }
              if (result.phoneNumber) { this.smsForm.setValue({customerPhoneNo: this.sharedService.formatPhoneNumber(result.phoneNumber)}); }
            }
            this.isAccountDetailsShown = true;
            this.isDeregisterAccountShown = this.accountRegistered && this.deregCmdSupported && !this.userContextService.hasReadOnlyAccess();
            this.alertTheUser("Processed", result.message || '');
          }
          this.isSpinnerHidden = true;
        },
        error: (error) => { this.customLoggerService.logException('csr.onSubmit()', 'CSR-2', error.message, 'CSR'); },
        complete: () => {}
      });
  }

  onSendEmail(value: string) {
    this.isSpinnerHidden = false;
    let customerEmailAddress = this.emailForm.value.customerEmailAddress;
    let enrollmentInvitationRequest = this.makeEnrollmentInvitationRequest('EMAIL', customerEmailAddress, '', this.csrInvitationParams);
    this.csrService.sendEnrollmentInvitation(this.currentBrand.brandCode, this.currentBrand.baseUrl, enrollmentInvitationRequest)
      .subscribe({
        next: (response) => {
          this.coachingHeader = this.coachingHeaderEmail;
          this.coachingBody = this.coachingBodyEmail;
          this.isCoachingShown = true;
          this.isSpinnerHidden = true;
          this.alertTheUser("Processed", "An email invitation has been successfuly sent to " + customerEmailAddress);
        },
        error: (error) => {
          this.customLoggerService.logException('csr.onSendEmal()', 'CSR-3', error.message, 'CSR');
          this.isSpinnerHidden = true;
          this.alertTheUser("Error", error.message);
        },
        complete: () => {}
      });
  }

  onSendSMS(value: string) {
    this.isSpinnerHidden = false;
    let customerPhoneNo = this.smsForm.value.customerPhoneNo;
    let enrollmentInvitationRequest = this.makeEnrollmentInvitationRequest('SMS', '', customerPhoneNo, this.csrInvitationParams);
    this.csrService.sendEnrollmentInvitation(this.currentBrand.brandCode, this.currentBrand.baseUrl, enrollmentInvitationRequest)
      .subscribe({
        next: (response) => {
          this.coachingHeader = this.coachingHeaderSMS;
          this.coachingBody = this.coachingBodySMS;
          this.isCoachingShown = true;
          this.isSpinnerHidden = true;
          this.alertTheUser("Processed", "A SMS invitation has been successfuly sent to " + customerPhoneNo);
        },
        error: (error) => {
          this.customLoggerService.logException('csr.onSendSMS()', 'CSR-4', error.message, 'CSR');
          this.isSpinnerHidden = true;
          this.alertTheUser("Error", error.message);
        },
        complete: () => {}
      });
  }

  onDeregisterAccount(value: string) {
    let accountNumber = this.accountForm.value.accountNumber;
    this.confirmationService.confirm({
      message: "Do you really want to deactivate account " + accountNumber + " from Walletron?",
      header: 'Warning',
      key: 'csr-account',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: "Yes",
      rejectVisible: true,
      acceptButtonStyleClass: "p-button-success wal-success p-button-rounded",
      rejectButtonStyleClass: "p-button-danger wal-danger p-button-rounded",
      accept: () => {
        this.isSpinnerHidden = false;
        this.csrService.deregisterAccount(this.currentBrand.brandCode, this.currentBrand.baseUrl, accountNumber)
        .subscribe({
          next: (response) => {
            this.newAccountCleanup();
            this.isSpinnerHidden = true;
            this.alertTheUser("Processed", "Account " + accountNumber + " has been successfuly deregistered from Walletron.");
          },
          error: (error) => {
            this.customLoggerService.logException('csr.onDeregisterAccount()', 'CSR-5', error.message, 'CSR');
            this.isSpinnerHidden = true;
            this.alertTheUser("Error", error.message);
          },
          complete: () => {}
        });
      }
    });
  }

  newBrandCleanup() {
    this.isSecondaryAccountIdShown = false;
    this.isTretiaryAccountIdShown = false;
    this.isCsrInstructShown = false;
    this.useSecondaryAccountId = false;
    this.secondaryAccountIdName = '';
    this.secondaryAccountIdLabel = 'Secondary Account Id:';
    this.useTertiaryAccountId = false;
    this.tertiaryAccountIdName = '';
    this.tertiaryAccountIdLabel = 'Tertiary Account Id:';
    this.csrInstructHeader = 'Walletron Background';
    this.csrInstructBody = '';
    this.deregCmdSupported = false;
    this.accountForm.reset();
  }

  newAccountCleanup() {
    this.isAccountDetailsShown = false;
    this.isSendEnrollmentLinksShown = false;
    this.isCoachingShown = false;
    this.isDeregisterAccountShown = false;
    this.accountFound = false;
    this.accountRegistered = false;
    this.message = '';
    this.accountFields = [];
    this.emailForm.reset();
    this.smsForm.reset();
  }

  makeEnrollmentInvitationRequest(channel: string, emailAddress: string, phoneNo: string, csrInvitationParams: AccountField<string>[] = []) {
    let params = new Array(0);
    if (this.useSecondaryAccountId) { params.push({key: this.secondaryAccountIdName, value: this.accountForm.value.secondaryAccountId}); }
    if (this.useTertiaryAccountId) { params.push({key: this.tertiaryAccountIdName, value: this.accountForm.value.tertiaryAccountId}); }
    if (csrInvitationParams && csrInvitationParams.length > 0) { 
      csrInvitationParams.forEach(item => {
        params.push({key: item.key, value: item.value});
      });
    }
    return {
      accountId: this.accountForm.value.accountNumber,
      emailAddr: emailAddress,
      phoneNum: phoneNo,
      channel: channel,
      params: params
    };
  }

  makeTitleMessage(currentBrand: IBrand) {
    var title = '';
    if (currentBrand.brandCode) {
      title += 'Brand: ' + currentBrand.brandName + ' ( ' + currentBrand.brandCode + ' ) >> ';
    }
    title += 'CSR';
    if (currentBrand.logoImageContent) {
      this.brandImage = 'data:' + currentBrand.logoImageType + ';base64,' + currentBrand.logoImageContent;
      this.isImagePreviewShown = true;
    }
    return title;
  }

  alertTheUser(header: string, message: string) {
    this.confirmationService.confirm({
      message: message,
      header: header,
      key: 'csr-account',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: "Ok",
      rejectVisible: false,
      acceptButtonStyleClass: "p-button-info wal-info p-button-rounded",
      accept: () => { return; }
    });
  }
}
